import type { ApiRequestOptions } from "@/api/generated/core/ApiRequestOptions";
import { ApiError } from "@/api/generated/core/ApiError";
import { OpenAPI } from "@/api/generated/core/OpenAPI";
import { normalizeApiError } from "@/api/apiError";
import { useNotificationStore } from "@/shared/composables/useNotificationStore";
import { i18n } from "@/i18n";

const ACCESS_DENIED_MESSAGE_KEY = "vde.access.denied.message";

async function resolveHeaders(
    headers: typeof OpenAPI.HEADERS,
    options: ApiRequestOptions
): Promise<Record<string, string>> {
    if (!headers) {
        return {};
    }

    if (typeof headers === "function") {
        return (await headers(options)) ?? {};
    }

    return headers;
}

function redirectToSignin(): void {
    const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.href = `/signin?returnUrl=${encodeURIComponent(returnUrl)}`;
}

export function configureGeneratedClient(): void {
    const previousHeaders = OpenAPI.HEADERS;

    OpenAPI.WITH_CREDENTIALS = true;
    OpenAPI.CREDENTIALS = "same-origin";
    OpenAPI.HEADERS = async (options: ApiRequestOptions) => ({
        ...(await resolveHeaders(previousHeaders, options)),
        "X-Requested-With": "XMLHttpRequest"
    });
}

export async function handleGeneratedApiError(error: unknown): Promise<never> {
    const normalized = normalizeApiError(error, getAccessDeniedFallbackMessage());

    if (normalized.status === 401 && !isDomainWorkspaceRequest(error)) {
        redirectToSignin();
    }

    if (normalized.status === 403) {
        const { addNotification } = useNotificationStore();
        addNotification(normalized.message || getAccessDeniedFallbackMessage(), "warning");
    }

    throw error;
}

function isDomainWorkspaceRequest(error: unknown): boolean {
    if (!(error instanceof ApiError)) {
        return false;
    }

    return error.request.method === "GET" && error.request.url === "/api/domain/{idShort}/workspace";
}

function getAccessDeniedFallbackMessage(): string {
    return i18n.global.t(ACCESS_DENIED_MESSAGE_KEY);
}
